import React from "react";
import { useContent } from "@/lib/content";

/* Illustrated schematic site map — pitches grouped by lake & type.
   Indicative layout (not to scale); real positions are allocated on arrival.
   If Justina uploads an official site plan via Content → Site Map, it replaces
   this illustration automatically. */

const C = {
  hs: "#3A322E", // hardstanding — charcoal
  grass: "#7A8B6F", // grass EHU — sage
  tent: "#B76E79", // tent — rose
  lodge: "#C9A47E", // lodges — gold
  water: "#BED6D3",
  waterStroke: "#9DBFB9",
  ink: "#3A322E",
  mute: "#6A5F58",
};

const Pitch = ({ x, y, n, color }) => (
  <g>
    <circle cx={x} cy={y} r={11} fill="#FFFFFF" stroke={color} strokeWidth={2} />
    <text x={x} y={y + 3.5} textAnchor="middle" fontSize={10} fontWeight={700} fill={color} fontFamily="Arial, sans-serif">
      {n}
    </text>
  </g>
);

const Lodge = ({ x, y, name }) => (
  <g>
    <rect x={x - 12} y={y - 12} width={24} height={24} rx={6} fill={C.lodge} />
    <path d={`M${x - 6} ${y + 1} L${x} ${y - 5} L${x + 6} ${y + 1} M${x - 4} ${y + 1} L${x - 4} ${y + 6} L${x + 4} ${y + 6} L${x + 4} ${y + 1}`}
      stroke="#0F0E0E" strokeWidth={1.6} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <text x={x + 20} y={y + 4} fontSize={12} fontStyle="italic" fill={C.ink} fontFamily="Georgia, serif">{name}</text>
  </g>
);

// Daisy Lake — hardstanding 1–10 along the east bank
const daisyHS = Array.from({ length: 10 }, (_, i) => ({
  n: i + 1,
  x: 438 + Math.sin((i / 9) * Math.PI) * 42,
  y: 132 + i * 27.5,
}));

// Walnut Lake — hardstanding 1–5 (guest-facing; ids walnut-hs-11…15)
const walnutHS = Array.from({ length: 5 }, (_, i) => ({
  n: i + 1,
  idn: i + 11,
  x: 588 + i * 66,
  y: 240 - Math.sin((i / 4) * Math.PI) * 16,
}));

// Walnut Lake — grass 1–12 down the east bank
const walnutGrass = Array.from({ length: 12 }, (_, i) => ({
  n: i + 1,
  x: 920 + Math.sin((i / 11) * Math.PI) * 18,
  y: 285 + i * 19,
}));

// Walnut Lake — tent 1–20 in the south meadow (two rows)
const walnutTent = Array.from({ length: 20 }, (_, i) => ({
  n: i + 1,
  x: 592 + (i % 10) * 38,
  y: i < 10 ? 552 : 596,
}));

const SiteMap = () => {
  const c = useContent();
  const customImage = c("sitemap.image");

  if (customImage) {
    return (
      <figure className="rounded-2xl border border-[#DDD1BE] bg-white p-4 shadow-sm" data-testid="sitemap-custom">
        <img src={customImage} alt="Walnut Lakes official site plan" className="w-full rounded-xl" />
        <figcaption className="mt-3 text-xs text-[#6A5F58] text-center">
          Official site plan · tell us your favourite pitch when you book and we'll reserve it if it's free.
        </figcaption>
      </figure>
    );
  }

  return (
    <div className="rounded-2xl border border-[#DDD1BE] bg-white p-4 md:p-6 shadow-sm" data-testid="sitemap-illustrated">
      <svg viewBox="0 0 1000 660" className="w-full h-auto" role="img" aria-label="Illustrated map of Walnut Lakes pitches">
        {/* paper */}
        <rect x={0} y={0} width={1000} height={660} rx={16} fill="#FBF7F1" />

        {/* dog walk loop */}
        <path d="M70,470 C60,560 140,620 260,622 C380,624 440,580 436,520 C432,462 360,436 250,438 C150,440 78,420 70,470 Z"
          fill="none" stroke="#B76E79" strokeWidth={1.6} strokeDasharray="7 6" opacity={0.7} />
        <text x={250} y={536} textAnchor="middle" fontSize={12} fontStyle="italic" fill={C.mute} fontFamily="Georgia, serif">
          ½-mile enclosed dog walk
        </text>

        {/* Daisy Lake */}
        <path d="M118,250 C112,152 196,92 280,96 C372,100 428,168 424,248 C420,332 348,392 262,388 C178,384 124,332 118,250 Z"
          fill={C.water} stroke={C.waterStroke} strokeWidth={2.5} />
        <text x={268} y={250} textAnchor="middle" fontSize={30} fontStyle="italic" fill="#5E8280" fontFamily="Georgia, serif">
          Daisy Lake
        </text>

        {/* Walnut Lake */}
        <path d="M548,378 C544,290 628,232 722,236 C826,240 898,306 894,384 C890,464 808,518 714,514 C618,510 552,458 548,378 Z"
          fill={C.water} stroke={C.waterStroke} strokeWidth={2.5} />
        <text x={718} y={382} textAnchor="middle" fontSize={30} fontStyle="italic" fill="#5E8280" fontFamily="Georgia, serif">
          Walnut Lake
        </text>

        {/* lodges on Daisy Lake's west bank */}
        <Lodge x={84} y={168} name="Heron Lodge" />
        <Lodge x={66} y={248} name="Kingfisher Lodge" />
        <Lodge x={84} y={328} name="Alpine Lodge" />

        {/* pitches */}
        {daisyHS.map((p) => <Pitch key={`dh-${p.n}`} {...p} color={C.hs} />)}
        {walnutHS.map((p) => <Pitch key={`wh-${p.n}`} {...p} color={C.hs} />)}
        {walnutGrass.map((p) => <Pitch key={`wg-${p.n}`} {...p} color={C.grass} />)}
        {walnutTent.map((p) => <Pitch key={`wt-${p.n}`} {...p} color={C.tent} />)}

        {/* zone captions */}
        <text x={516} y={118} fontSize={11} letterSpacing={2} fill={C.mute} fontFamily="Arial, sans-serif">HARDSTANDING 1–10 · EHU</text>
        <text x={618} y={206} fontSize={11} letterSpacing={2} fill={C.mute} fontFamily="Arial, sans-serif">HARDSTANDING 1–5 · EHU</text>
        <text x={922} y={528} textAnchor="middle" fontSize={11} letterSpacing={2} fill={C.mute} fontFamily="Arial, sans-serif">GRASS 1–12 · EHU</text>
        <text x={760} y={640} fontSize={11} letterSpacing={2} fill={C.mute} fontFamily="Arial, sans-serif">TENT 1–20 · NO EHU</text>

        {/* entrance */}
        <path d="M492,640 L492,614 M486,622 L492,614 L498,622" stroke={C.ink} strokeWidth={2} fill="none" strokeLinecap="round" />
        <text x={508} y={636} fontSize={12} fontStyle="italic" fill={C.ink} fontFamily="Georgia, serif">Park entrance · Main Road</text>
      </svg>

      {/* legend */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.14em] text-[#3A322E]">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border-2" style={{ borderColor: C.hs }} /> Hardstanding · EHU · £30/night</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border-2" style={{ borderColor: C.grass }} /> Grass · EHU · £30/night</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border-2" style={{ borderColor: C.tent }} /> Tent · no EHU · £24/night</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-md" style={{ background: C.lodge }} /> Lakeside lodges</span>
      </div>
      <p className="mt-3 text-center text-xs text-[#6A5F58]">
        Indicative layout, not to scale · pitches are allocated on arrival — tell us your favourite when you book (or call us) and we'll reserve it if it's free.
      </p>
    </div>
  );
};

export default SiteMap;

/* Shared map geometry for the interactive pick-your-pitch page
   (/pick-your-pitch) — same coordinates the Policies page illustration uses. */
export const MAP_COLORS = C;
export const MAP_PITCH_GROUPS = [
  { key: "daisyHS", idPrefix: "daisy-hs", color: C.hs, points: daisyHS },
  { key: "walnutHS", idPrefix: "walnut-hs", color: C.hs, points: walnutHS },
  { key: "walnutGrass", idPrefix: "walnut-grass", color: C.grass, points: walnutGrass },
  { key: "walnutTent", idPrefix: "walnut-tent", color: C.tent, points: walnutTent },
];
export const MAP_LODGES = [
  { id: "lodge-heron", name: "Heron Lodge", x: 84, y: 168 },
  { id: "lodge-kingfisher", name: "Kingfisher Lodge", x: 66, y: 248 },
  { id: "lodge-alpine", name: "Alpine Lodge", x: 84, y: 328 },
];
